import { useState, useEffect, useCallback } from "react";
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { addUpdateFCMToken } from "../services/workspace/global.service";
import { firebaseConfig, validateFirebaseConfig } from "../utils/firebaseConfig";

// Validate Firebase configuration on import
const configValidation = validateFirebaseConfig();

let app = null;
let messaging = null;

// Only initialize Firebase if configuration is valid
if (configValidation.isValid) {
  try {
    app = initializeApp(firebaseConfig);
    
    // Check for support before initializing messaging
    if (
      typeof window !== "undefined" &&
      "Notification" in window &&
      "serviceWorker" in navigator &&
      "PushManager" in window
    ) {
      try {
        messaging = getMessaging(app);
      } catch (err) {
        console.error("Error initializing messaging:", err);
      }
    }
  } catch (err) {
    console.error("Error initializing Firebase app:", err);
  }
} else {
  if (process.env.NODE_ENV === 'development') {
    console.warn("Firebase configuration is incomplete. Please check your environment variables.");
  }
}

const useFirebaseMessaging = () => {
  const [permission, setPermission] = useState(() => {
    if (typeof window !== "undefined" && "Notification" in window) {
      return Notification.permission;
    }
    return "default";
  });

  const [token, setToken] = useState(null);
  const [message, setMessage] = useState(null);
  const [serviceWorkerRegistered, setServiceWorkerRegistered] = useState(false);

  const getPseudoDeviceId = () => {
    const data = navigator.userAgent + navigator.platform;
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash |= 0; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16); // Absolute value and hexadecimal
  };

  // Register Service Worker
  const registerServiceWorker = useCallback(async () => {
    if (!("serviceWorker" in navigator)) {
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js"
      );

      // Wait for service worker to be ready, then send Firebase config
      // Use a one-time event listener to send config when service worker is ready
      const sendConfigToSW = () => {
        if (registration.active && configValidation.isValid) {
          registration.active.postMessage({
            type: 'FIREBASE_CONFIG',
            config: firebaseConfig
          });
        } else if (registration.installing) {
          registration.installing.addEventListener('statechange', () => {
            if (registration.installing.state === 'activated' && configValidation.isValid) {
              registration.active?.postMessage({
                type: 'FIREBASE_CONFIG',
                config: firebaseConfig
              });
            }
          });
        }
      };

      if (registration.installing) {
        registration.installing.addEventListener('statechange', sendConfigToSW);
      } else if (registration.active) {
        sendConfigToSW();
      }

      setServiceWorkerRegistered(true);
      return registration;
    } catch (error) {
      // Only log error if it's not a missing file error (which is expected in some cases)
      if (error.message && !error.message.includes('MIME type')) {
      }
      setServiceWorkerRegistered(false);
      return null;
    }
  }, []);

  // Request Notification Permission
  const requestNotificationPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      console.error("This browser does not support notifications.");
      setPermission("unsupported");
      return "unsupported";
    }

    if (permission === "granted" || permission === "denied") {
      // console.log(`Notification permission is already ${permission}.`);
      return permission;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result); // Save the permission state
      return result;
    } catch (error) {
      console.error(
        "An error occurred while requesting notification permission:",
        error
      );
      setPermission("error");
      return "error";
    }
  }, [permission]);

  // Request Firebase Token
  const requestForToken = useCallback(async () => {
    if (permission !== "granted") {
      console.warn(
        "Notification permission not granted. Unable to retrieve FCM token."
      );
      return null;
    }

    try {
      const registration = await registerServiceWorker();
      if (!registration) {
        return null;
      }
      const currentToken = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_PUBLIC_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (currentToken) {
        const device_id = getPseudoDeviceId();
        setToken(currentToken);
        try {
          const payload = {
            device_token: currentToken,
            device_type: "web",
            device_id,
          };
          await addUpdateFCMToken(payload);
        } catch (error) {
          console.error("Error uploading FCM token:", error);
        }
      } else {
        console.warn(
          "No FCM token available. Request permission to generate one."
        );
      }
      return currentToken;
    } catch (error) {
      console.error("An error occurred while retrieving the FCM token:", error);
      return null;
    }
  }, [permission, registerServiceWorker]);

  // Handle Incoming Messages
  useEffect(() => {
    if (!messaging) {
      console.warn("Messaging is not initialized or not supported in this browser.");
      return;
    }

    const unsubscribe = onMessage(messaging, (payload) => {
      setMessage(payload);
    });

    return () => unsubscribe();
  }, []);

  // Automatically request permission and register the service worker on mount
  useEffect(() => {
    const initializeMessaging = async () => {
      await requestNotificationPermission();
      await registerServiceWorker();
    };

    initializeMessaging();
  }, [requestNotificationPermission, registerServiceWorker]);

  return {
    permission,
    token,
    message,
    serviceWorkerRegistered,
    requestNotificationPermission,
    requestForToken,
  };
};

export default useFirebaseMessaging;
